export interface RejectionReasonFieldError {
  [fieldKey: string]: string;
}

export interface RejectionReasonStepError {
  step: number;
  fields: RejectionReasonFieldError[];
}

export interface RejectionReasonPayload {
  errors: RejectionReasonStepError[];
}

export const REJECTION_STEP = {
  INFO: 2,
  DOCUMENTS: 3,
  RESUME: 4,
} as const;

export const REJECTION_FIELD_LABELS: Record<string, string> = {
  first_name: "نام",
  last_name: "نام خانوادگی",
  national_code: "کد ملی",
  province: "استان",
  city: "شهر",
  school_name: "نام مدرسه",
  teaching_grade: "پایه تدریس",
  education_grade: "پایه تحصیلی",
  phone_number: "شماره موبایل",
  email: "ایمیل",
  identity_file: "عکس کارت ملی",
  employment_decree_file: "حکم کارگزینی",
  resume_file: "رزومه",
  resume: "رزومه",
};

export const STUDENT_REJECTION_FIELDS: {
  step: number;
  key: string;
}[] = [
  { step: REJECTION_STEP.INFO, key: "first_name" },
  { step: REJECTION_STEP.INFO, key: "last_name" },
  { step: REJECTION_STEP.INFO, key: "province" },
  { step: REJECTION_STEP.INFO, key: "city" },
  { step: REJECTION_STEP.INFO, key: "education_grade" },
  { step: REJECTION_STEP.INFO, key: "phone_number" },
  { step: REJECTION_STEP.INFO, key: "email" },
];

export const TEACHER_REJECTION_FIELDS: {
  step: number;
  key: string;
}[] = [
  { step: REJECTION_STEP.INFO, key: "first_name" },
  { step: REJECTION_STEP.INFO, key: "last_name" },
  { step: REJECTION_STEP.INFO, key: "national_code" },
  { step: REJECTION_STEP.INFO, key: "province" },
  { step: REJECTION_STEP.INFO, key: "city" },
  { step: REJECTION_STEP.INFO, key: "school_name" },
  { step: REJECTION_STEP.INFO, key: "teaching_grade" },
  { step: REJECTION_STEP.INFO, key: "phone_number" },
  { step: REJECTION_STEP.INFO, key: "email" },
  { step: REJECTION_STEP.DOCUMENTS, key: "identity_file" },
  { step: REJECTION_STEP.DOCUMENTS, key: "employment_decree_file" },
  { step: REJECTION_STEP.RESUME, key: "resume_file" },
];

export const REJECTION_STEP_LABELS: Record<number, string> = {
  [REJECTION_STEP.INFO]: "اطلاعات شخصی",
  [REJECTION_STEP.DOCUMENTS]: "مدارک هویتی",
  [REJECTION_STEP.RESUME]: "رزومه",
};

export interface RejectionFieldState {
  checked: boolean;
  reason: string;
}

export type RejectionFormState = Record<string, RejectionFieldState>;

export function createEmptyRejectionForm(
  fields: { step: number; key: string }[],
): RejectionFormState {
  return fields.reduce<RejectionFormState>((acc, field) => {
    acc[field.key] = { checked: false, reason: "" };
    return acc;
  }, {});
}

export function getRejectionFieldsForRole(role: "0" | "1" | "2") {
  return role === "0" ? STUDENT_REJECTION_FIELDS : TEACHER_REJECTION_FIELDS;
}

export function getRejectionStepsForRole(role: "0" | "1" | "2"): number[] {
  const fields = getRejectionFieldsForRole(role);
  return Array.from(new Set(fields.map((field) => field.step))).sort(
    (a, b) => a - b,
  );
}

export function buildRejectionReasonPayload(
  form: RejectionFormState,
  role: "0" | "1" | "2",
): RejectionReasonPayload {
  const fields = getRejectionFieldsForRole(role);
  const stepMap = new Map<number, RejectionReasonFieldError[]>();

  fields.forEach(({ step, key }) => {
    const fieldState = form[key];
    if (!fieldState?.checked || !fieldState.reason.trim()) return;

    const existing = stepMap.get(step) || [];
    existing.push({ [key]: fieldState.reason.trim() });
    stepMap.set(step, existing);
  });

  return {
    errors: Array.from(stepMap.entries())
      .sort(([stepA], [stepB]) => stepA - stepB)
      .map(([step, stepFields]) => ({
        step,
        fields: stepFields,
      })),
  };
}

export function hasValidRejectionSelection(form: RejectionFormState): boolean {
  return Object.values(form).some(
    (field) => field.checked && field.reason.trim().length > 0,
  );
}

export function countSelectedRejectionFields(form: RejectionFormState): number {
  return Object.values(form).filter(
    (field) => field.checked && field.reason.trim().length > 0,
  ).length;
}

export function parseRejectionReason(
  value: unknown,
): RejectionReasonPayload | null {
  if (!value) return null;

  if (typeof value === "object" && value !== null && "errors" in value) {
    const payload = value as RejectionReasonPayload;
    return Array.isArray(payload.errors) ? payload : null;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      return parseRejectionReason(parsed);
    } catch {
      return null;
    }
  }

  return null;
}

export function getRejectionFieldLabel(fieldKey: string): string {
  return REJECTION_FIELD_LABELS[fieldKey] || fieldKey;
}

export function getRejectionStepLabel(step: number): string {
  return REJECTION_STEP_LABELS[step] || `مرحله ${step}`;
}
